import { Checkbox, Form, Grid, NumberField, Select, Text, TextField } from '@approvalmax/ui/src/components';
import { netSuiteConstants } from 'modules/common/constants';
import { domain } from 'modules/data';
import { useVendorTemplateFieldSettings } from 'pages/requestForm/containers/netsuite/hooks';
import {
    isFieldMandatory,
    isFieldVisible,
} from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { memo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormValues } from '../../NetSuiteVendorCreationPopupContent.types';
import { useAccountsItems, useIncotermItems } from './FinancialInformation.hooks';
import { messages } from './FinancialInformation.messages';
import { FinancialInformationProps } from './FinancialInformation.types';

export const FinancialInformation = memo<FinancialInformationProps>((props) => {
    const { terms, withTaxNumber, currencyPrecision } = props;

    const fieldSettings = useVendorTemplateFieldSettings();

    const primarySubsidiaryId = useWatch<FormValues, 'primarySubsidiaryId'>({ name: 'primarySubsidiaryId' });

    const {
        items: expenseAccountItems,
        isFetching: isExpenseAccountsFetching,
        trigger: expenseAccountsTrigger,
        setQuery: setExpenseAccountsQuery,
    } = useAccountsItems(domain.NetSuiteAccountTypeGroup.Expense, primarySubsidiaryId ?? undefined);

    const {
        items: payablesAccountItems,
        isFetching: isPayablesAccountsFetching,
        trigger: payablesAccountsTrigger,
        setQuery: setPayablesAccountsQuery,
    } = useAccountsItems(domain.NetSuiteAccountTypeGroup.Payable, primarySubsidiaryId ?? undefined);

    const {
        items: incotermItems,
        isFetching: isIncotermsFetching,
        trigger: incotermsTrigger,
        setQuery: setIncotermsQuery,
    } = useIncotermItems();

    const hasFinancialInformationSection =
        isFieldVisible(fieldSettings.legalName) ||
        isFieldVisible(fieldSettings.accountNumber) ||
        isFieldVisible(fieldSettings.defaultExpenseAccount) ||
        isFieldVisible(fieldSettings.defaultPayablesAccount) ||
        isFieldVisible(fieldSettings.terms) ||
        isFieldVisible(fieldSettings.creditLimit) ||
        isFieldVisible(fieldSettings.incoterm) ||
        isFieldVisible(fieldSettings.taxNumber) ||
        isFieldVisible(fieldSettings.is1099Eligible) ||
        isFieldVisible(fieldSettings.isJobResource);

    if (!hasFinancialInformationSection) {
        return null;
    }

    return (
        <Form.Part>
            <Text font='headline' fontSize='xxsmall' fontWeight='medium' color='midnight80' spacing='20 0'>
                {messages.financialInformationSection}
            </Text>

            <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                {isFieldVisible(fieldSettings.legalName) && (
                    <TextField
                        label={messages.legalName}
                        required={isFieldMandatory(fieldSettings.legalName)}
                        name='legalName'
                        placeholder={messages.inputPlaceholder}
                        maxLength={netSuiteConstants.legalNameMaxLength}
                    />
                )}

                {isFieldVisible(fieldSettings.accountNumber) && (
                    <TextField
                        label={messages.accountNumber}
                        required={isFieldMandatory(fieldSettings.accountNumber)}
                        name='accountNumber'
                        placeholder={messages.inputPlaceholder}
                        maxLength={netSuiteConstants.accountNumberMaxLength}
                    />
                )}

                {isFieldVisible(fieldSettings.defaultExpenseAccount) && (
                    <Select
                        label={messages.defaultExpenseAccount}
                        required={isFieldMandatory(fieldSettings.defaultExpenseAccount)}
                        name='defaultExpenseAccountId'
                        placeholder={messages.selectPlaceholder}
                        preventSearch
                        autocomplete
                        items={expenseAccountItems}
                        onFocus={expenseAccountsTrigger}
                        onInputChange={setExpenseAccountsQuery}
                        progress={isExpenseAccountsFetching}
                        clearable={!isFieldMandatory(fieldSettings.defaultExpenseAccount)}
                    />
                )}

                {isFieldVisible(fieldSettings.defaultPayablesAccount) && (
                    <Select
                        label={messages.defaultPayablesAccount}
                        required={isFieldMandatory(fieldSettings.defaultPayablesAccount)}
                        name='defaultPayablesAccountId'
                        placeholder={messages.selectPlaceholder}
                        preventSearch
                        autocomplete
                        items={payablesAccountItems}
                        onFocus={payablesAccountsTrigger}
                        onInputChange={setPayablesAccountsQuery}
                        progress={isPayablesAccountsFetching}
                        clearable={!isFieldMandatory(fieldSettings.defaultPayablesAccount)}
                    />
                )}

                {isFieldVisible(fieldSettings.terms) && (
                    <Select
                        label={messages.terms}
                        required={isFieldMandatory(fieldSettings.terms)}
                        name='termsId'
                        placeholder={messages.selectPlaceholder}
                        autocomplete
                        items={terms}
                        itemNameKey='text'
                        clearable={!isFieldMandatory(fieldSettings.terms)}
                    />
                )}

                {isFieldVisible(fieldSettings.creditLimit) && (
                    <NumberField
                        label={messages.creditLimit}
                        required={isFieldMandatory(fieldSettings.creditLimit)}
                        name='creditLimit'
                        placeholder={messages.inputPlaceholder}
                        max={netSuiteConstants.maxCreditLimit}
                        precision={currencyPrecision}
                    />
                )}

                {isFieldVisible(fieldSettings.incoterm) && (
                    <Select
                        label={messages.incoterm}
                        required={isFieldMandatory(fieldSettings.incoterm)}
                        name='incotermName'
                        placeholder={messages.selectPlaceholder}
                        preventSearch
                        autocomplete
                        itemIdKey='name'
                        itemNameKey='name'
                        items={incotermItems}
                        onFocus={incotermsTrigger}
                        onInputChange={setIncotermsQuery}
                        progress={isIncotermsFetching}
                        clearable={!isFieldMandatory(fieldSettings.incoterm)}
                    />
                )}

                {withTaxNumber && (
                    <TextField
                        label={messages.taxNumber}
                        required={isFieldMandatory(fieldSettings.taxNumber)}
                        name='taxNumber'
                        placeholder={messages.inputPlaceholder}
                        maxLength={netSuiteConstants.taxNumberMaxLength}
                    />
                )}

                {isFieldVisible(fieldSettings.is1099Eligible) && (
                    <Grid alignItems='center' padding={withTaxNumber ? '0' : '16 0 0'}>
                        <Checkbox name='is1099Eligible'>{messages.is1099Eligible}</Checkbox>
                    </Grid>
                )}

                {isFieldVisible(fieldSettings.isJobResource) && (
                    <Grid alignItems='center'>
                        <Checkbox name='isJobResource'>{messages.isJobResource}</Checkbox>
                    </Grid>
                )}
            </Grid>
        </Form.Part>
    );
});

FinancialInformation.displayName = 'FinancialInformation';
